/**
 * CSVサンプルファイル生成のユーティリティ
 */

export interface CSVSampleData {
  filename: string;
  content: string;
  description: string;
}

/**
 * 標準フォーマットのCSVサンプルを生成
 */
export const generateStandardCSVSample = (): CSVSampleData => {
  // 現在の会計年度に基づいて動的にサンプル年度を生成
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 0-indexed to 1-indexed
  
  // 4月開始の会計年度を仮定してサンプル年を算出
  const sampleYear = currentMonth >= 4 ? currentYear : currentYear - 1;
  const sampleMonth = 4; // 4月のサンプルデータ
  
  const header = '日付,カテゴリ,摘要,金額,収支,メモ';
  const sampleRows = [
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-01,管理費,管理費収入,350000,収入,${sampleMonth}月分管理費`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-03,光熱費,電気代,15000,支出,共用部電気代`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-05,積立金,積立金収入,200000,収入,${sampleMonth}月分積立金`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-10,清掃費,清掃委託費,45000,支出,月次清掃`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-15,設備維持費,エレベーター点検費,25000,支出,定期点検`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-20,管理費,管理費収入,350000,収入,遅納分管理費`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-25,光熱費,水道代,8000,支出,共用部水道代`
  ];
  
  const content = [header, ...sampleRows].join('\n');
  
  return {
    filename: 'standard_format_sample.csv',
    content,
    description: '標準フォーマット（日付・カテゴリ・摘要・金額・収支・メモ）'
  };
};

/**
 * 年度サマリーフォーマットのCSVサンプルを生成
 */
export const generateYearSummaryCSVSample = (): CSVSampleData => {
  // 現在の会計年度に基づいて動的にサンプル年度を生成
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 0-indexed to 1-indexed
  
  // 4月開始の会計年度を仮定してサンプル年を算出
  const sampleYear = currentMonth >= 4 ? currentYear : currentYear - 1;
  
  const header = '年,カテゴリ,摘要,金額,収支,メモ';
  const sampleRows = [
    `${sampleYear},管理費,管理費収入,4200000,収入,${sampleYear}年度管理費合計`,
    `${sampleYear},積立金,積立金収入,2400000,収入,${sampleYear}年度積立金合計`,
    `${sampleYear},光熱費,電気代,180000,支出,${sampleYear}年度電気代合計`,
    `${sampleYear},光熱費,水道代,96000,支出,${sampleYear}年度水道代合計`,
    `${sampleYear},清掃費,清掃委託費,540000,支出,${sampleYear}年度清掃費合計`,
    `${sampleYear},設備維持費,エレベーター保守,300000,支出,${sampleYear}年度保守費合計`,
    `${sampleYear},その他,事務用品,25000,支出,${sampleYear}年度事務用品合計`
  ];
  
  const content = [header, ...sampleRows].join('\n');
  
  return {
    filename: 'year_summary_sample.csv',
    content,
    description: '年度サマリーフォーマット（年・カテゴリ・摘要・金額・収支・メモ）'
  };
};

/**
 * 収支分離フォーマットのCSVサンプルを生成
 */
export const generateIncomeExpenseSplitCSVSample = (): CSVSampleData => {
  // 現在の会計年度に基づいて動的にサンプル年度を生成
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 0-indexed to 1-indexed
  
  // 4月開始の会計年度を仮定してサンプル年を算出
  const sampleYear = currentMonth >= 4 ? currentYear : currentYear - 1;
  const sampleMonth = 4; // 4月のサンプルデータ
  
  const header = '日付,収入,支出,カテゴリ,摘要';
  const sampleRows = [
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-01,350000,,管理費,${sampleMonth}月分管理費`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-03,,15000,光熱費,共用部電気代`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-05,200000,,積立金,${sampleMonth}月分積立金`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-10,,45000,清掃費,月次清掃`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-15,,25000,設備維持費,エレベーター点検`,
    `${sampleYear}-${String(sampleMonth).padStart(2, '0')}-25,,8000,光熱費,共用部水道代`
  ];
  
  const content = [header, ...sampleRows].join('\n');
  
  return {
    filename: 'income_expense_split_sample.csv',
    content,
    description: '収支分離フォーマット（日付・収入・支出・カテゴリ・摘要）'
  };
};

/**
 * CSVファイルをダウンロードする
 */
export const downloadCSVSample = (sampleData: CSVSampleData): void => {
  // BOM付きUTF-8でエンコード（Excelでの文字化け防止）
  const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
  const content = new TextEncoder().encode(sampleData.content);
  const blob = new Blob([bom, content], { type: 'text/csv;charset=utf-8' });
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = sampleData.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * 全てのサンプルフォーマットを取得
 */
export const getAllCSVSamples = (): CSVSampleData[] => {
  return [
    generateStandardCSVSample(),
    generateYearSummaryCSVSample(),
    generateIncomeExpenseSplitCSVSample()
  ];
};